import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import { format, isValid, parseISO } from "date-fns";
import { useCookies } from "react-cookie";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import { TransportRowAction, TransportRowAction2Buttons } from "../shared/transportRow";
import SbbQuickSelection from "./SbbQuickSelection";
import SbbPassengersForm from "./SbbPassengersForm";
import SbbSummary from "./SbbSummary";
import AppBookingError from "../../../components/AppBookingError";
import PaymentModal from "../../../components/Payment/PaymentModal";
import AppTimeComponent from "../../../components/AppTimeComponent";
import AppCustomRouteAsync from "../../../components/AppCustomRouteAsync";
import AppButton from "../../../components/ui/AppButton";
import LastMileInfo from "../../../components/LastMileInfo";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// helpers
import { checkForEmptyNames } from "./helpers/checkForEmptyNames";
import { getAirlineSbbPrefillData } from "./helpers/getAirlineSbbPrefillData";
import { mapAirlineStationToRouteOption } from "./helpers/mapAirlineStationToRouteOption";
import { parseAirlineDepartureTime } from "../../../common/utils/helpers/parseAirlineDepartureTime";
import { hoursUpdate } from "../../../components/AppTimeComponent/helpers/updateHours";
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { checkIsItInFuture } from "../../../common/utils/helpers/checkIsItInFuture";
import { isObjectEmpty } from "../../../common/utils/helpers/isObjectEmpty";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import {
  buildSbbNavigationState,
  clearPersistedAirlineSbbRouterState,
  persistAirlineSbbFromNavigationState,
  resolveSbbTransportApiContext
} from "../../../common/utils/transport/sbbTransportApiContext";
// hooks
import { useActions } from "../../../common/hooks/useActions";
// actions
import { sbbActionCreators } from "./sbbModule";
import { selectApiBaseUrl } from "../../../common/store/modules/globalModule";
// icons
import { dateIcon, serviceClassIcon, timeIcon, directionIcon } from "../../../common/vector";
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";

interface SbbTypes {
  isZurichCard?: boolean;
}

type OptionType = { value: string; label: string; stationId: number };

const outwardDirection = "OUTWARD";
const roundDirection = "ROUND";

export default function Sbb({ isZurichCard }: SbbTypes) {
  const theme = useTheme();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const language = localStorage.getItem("i18nextLng") || "en";
  const [searchParams, setSearchParams] = useSearchParams();
  const quickSelectParam: string | null = searchParams.get("qs");
  const fromParam: string | null = searchParams.get("from");
  const toParam: string | null = searchParams.get("to");

  useLayoutEffect(() => {
    persistAirlineSbbFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildSbbNavigationState(location.state), [location.state]);

  const airlineSharedRoute = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as
      | {
          meta?: { source?: string };
          shared?: {
            milesAndMore?: string;
            arrivalDestination?: {
              placeId?: string | null;
              formattedAddress?: { secondary_text?: string | null } | null;
            } | null;
            finalDestination?: {
              placeId?: string | null;
              description?: string | null;
            } | null;
          };
        }
      | null
      | undefined;

    if (nav?.meta?.source !== "airline") {
      return {
        milesAndMore: "",
        departureAddress: null as string | null,
        departureAddressId: null as string | null,
        destinationAddress: null as string | null,
        destinationAddressId: null as string | null
      };
    }

    return {
      milesAndMore: nav.shared?.milesAndMore ?? "",
      departureAddress: nav.shared?.arrivalDestination?.formattedAddress?.secondary_text ?? null,
      departureAddressId: nav.shared?.arrivalDestination?.placeId ?? null,
      destinationAddress: nav.shared?.finalDestination?.description ?? null,
      destinationAddressId: nav.shared?.finalDestination?.placeId ?? null
    };
  }, [location.state, restoredNavState]);

  const airlinePrefillData = useMemo(
    () => getAirlineSbbPrefillData(restoredNavState ?? location.state),
    [location.state, restoredNavState]
  );

  const sbbTransportApiContext = useMemo(
    () => resolveSbbTransportApiContext(Boolean(isZurichCard), location.state),
    [isZurichCard, location.state]
  );
  const isAirlineSource = Boolean(airlinePrefillData);
  const airlineDeparture = airlinePrefillData?.departure || null;
  const airlineDestination = airlinePrefillData?.destination || null;
  const airlineDepartureDate = airlinePrefillData?.departureDate || null;
  const airlineDepartureTime = airlinePrefillData?.departureTime || null;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [serviceClass, setServiceClass] = useState(2);
  const [direction, setDirection] = useState(outwardDirection);
  const [sbbHours, setSbbHours] = useState(new Date().getHours());
  const [sbbMinutes, setSbbMinutes] = useState(new Date().getMinutes());
  // Routes
  const [selectedFromStation, setSelectedFromStation] = useState<OptionType | null>(null);
  const [selectedToStation, setSelectedToStation] = useState<OptionType | null>(null);
  const [disableCustomRouteAsync, setDisableCustomRouteAsync] = useState(false);
  const [selectedToStationCoords, setSelectedToStationCoords] = useState<{ lat: number; lng: number } | null>(null);
  // Airline only: after the user changes or clears stations, do not re-apply the initial airline route prefill.
  const skipAirlineRoutePrefillRef = useRef(false);

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const sbbBookingResponseData = useSelector((state: any) => state.sbb.sbbBookingResponseData);
  const sbbQuickSelection = useSelector((state: any) => state.sbb.sbbQuickSelection);
  const quickSelectionError = useSelector((state: any) => state.sbb.quickSelectionError);
  const sbbCustomRouteCode = useSelector((state: any) => state.sbb.sbbCustomRouteCode);
  const sbbActiveOffer = useSelector((state: any) => state.sbb.sbbActiveOffer);
  const sbbData = useSelector((state: any) => state.sbb.sbbData);
  const selectedStationFrom = useSelector((state: any) => state.sbb.selectedStationFrom);
  const selectedStationTo = useSelector((state: any) => state.sbb.selectedStationTo);
  const passengerList = useSelector((state: any) => state.sbb.passengerList);
  const errorPrebook = useSelector((state: any) => state.sbb.errorPrebook);
  const prebookPriceInfo = useSelector((state: any) => state.sbb.prebookPriceInfo);
  const sbbBookingError = useSelector((state: any) => state.sbb.sbbBookingError);
  const showSbbPaymentModal = useSelector((state: any) => state.sbb.showSbbPaymentModal);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const apiUrl = useSelector(selectApiBaseUrl);
  const lastMileRoute = useSelector((state: any) => state.sbb.lastMileRoute);
  const isLastMileRouteLoading = useSelector((state: any) => state.sbb.isLastMileRouteLoading);
  const lastMileRouteError = useSelector((state: any) => state.sbb.lastMileRouteError);

  // actions
  const getSbbInfo = useActions(sbbActionCreators?.getSbbInfoAction, []);
  const searchSbbStations = useActions(sbbActionCreators?.searchSbbStationsAction, []);
  const getPriceSbbPrebook = useActions(sbbActionCreators?.getPriceSbbPrebookAction, []);
  const buySbbTicket = useActions(sbbActionCreators?.buySbbTicketAction, []);
  const setSbbActiveOffer = useActions(sbbActionCreators?.setSbbActiveOfferAction, []);
  const resetSbbState = useActions(sbbActionCreators?.resetSbbStateAction, []);
  const toggleSbbPaymentModal = useActions(sbbActionCreators?.toggleSbbPaymentModalAction, []);
  const toggleSbbBookingError = useActions(sbbActionCreators?.toggleSbbBookingErrorAction, []);
  const getLastMileRoute = useActions(sbbActionCreators?.getLastMileRouteAction, []);

  const navigateSbb = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      navigate(to, { replace: options?.replace, state: buildSbbNavigationState(location.state) });
    },
    [navigate, location.state]
  );

  useEffect(() => {
    updateMinutes(setSbbMinutes, setSbbHours, "initial", 20, sbbHours, sbbMinutes);

    return () => {
      resetSbbState();
      clearPersistedAirlineSbbRouterState();
    };
  }, []);

  useEffect(() => {
    if (tenantInfo) {
      getSbbInfo(isZurichCard, sbbTransportApiContext);
    }
  }, [tenantInfo, isZurichCard, sbbTransportApiContext]);

  // --- AIRLINE EXTENSION ---

  useEffect(() => {
    // Airline extension: prefill date on initial load; hotel flow remains untouched.
    if (!airlineDepartureDate) {
      return;
    }

    const parsedDate = parseISO(airlineDepartureDate);
    if (!isValid(parsedDate)) {
      return;
    }

    setSelectedDate(parsedDate);
  }, [airlineDepartureDate]);

  useEffect(() => {
    // Airline extension: prefill time on initial load; no-op when data is missing.
    const parsedTime = parseAirlineDepartureTime(airlineDepartureTime);
    if (!parsedTime) {
      return;
    }

    setSbbHours(parsedTime.hours);
    setSbbMinutes(parsedTime.minutes);
  }, [airlineDepartureTime]);

  useEffect(() => {
    // Airline extension: route preselection reuses existing URL/search+autocomplete flow
    // so state updates and side effects match manual user selections.
    // Runs once on entry only — not when the user clears both fields (empty URL params).
    if (
      skipAirlineRoutePrefillRef.current ||
      !isAirlineSource ||
      !airlineDeparture?.name ||
      !airlineDestination?.name ||
      fromParam ||
      toParam
    ) {
      return;
    }

    setSearchParams(
      { from: airlineDeparture.name, to: airlineDestination.name },
      { replace: true, state: buildSbbNavigationState(location.state) }
    );
  }, [isAirlineSource, airlineDeparture, airlineDestination, fromParam, toParam, location.state, setSearchParams]);

  useEffect(() => {
    // Airline extension: set route inputs immediately from initial navigation payload,
    // then keep URL in sync through existing behavior.
    if (
      skipAirlineRoutePrefillRef.current ||
      !isAirlineSource ||
      !airlineDeparture?.name ||
      !airlineDestination?.name
    ) {
      return;
    }

    const departureStation = mapAirlineStationToRouteOption(airlineDeparture);
    const destinationStation = mapAirlineStationToRouteOption(airlineDestination);
    if (!departureStation || !destinationStation) {
      return;
    }

    setSelectedFromStation(departureStation);
    setSelectedToStation(destinationStation);
    if (typeof airlineDestination?.latitude === "number" && typeof airlineDestination?.longitude === "number") {
      setSelectedToStationCoords({ lat: airlineDestination.latitude, lng: airlineDestination.longitude });
    }
    setDisableCustomRouteAsync(false);
  }, [isAirlineSource, airlineDeparture, airlineDestination]);

  // --- END OF AIRLINE EXTENSION ---

  // --- LAST MILE ---
  const isAirlineTransferHub = sbbTransportApiContext?.mode === "airline";
  const destinationPlaceId = airlineSharedRoute?.destinationAddressId || null;
  const destinationName = airlineSharedRoute?.destinationAddress || "";

  useEffect(() => {
    if (
      !isAirlineTransferHub ||
      !destinationPlaceId ||
      typeof selectedToStationCoords?.lat !== "number" ||
      typeof selectedToStationCoords?.lng !== "number"
    ) {
      return;
    }

    getLastMileRoute({
      departureLatitude: selectedToStationCoords.lat,
      departureLongitude: selectedToStationCoords.lng,
      destinationPlaceId,
      travelMode: "walking"
    });
  }, [isAirlineTransferHub, destinationPlaceId, selectedToStationCoords, getLastMileRoute]);
  // --- END OF LAST MILE ---

  const sbbServicePath = isZurichCard ? "zurich_card" : "sbb";

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: `/transport/${sbbServicePath}`,
    getNavigationState: () => buildSbbNavigationState(locationRef.current.state),
    iataCode: sbbTransportApiContext.mode === "airline" ? sbbTransportApiContext.iataCode : null
  });

  useEffect(() => {
    if (quickSelectParam) {
      const selectedOffer = sbbQuickSelection?.find((sbbOffer: any): any => sbbOffer?.code === quickSelectParam);
      setSbbActiveOffer(selectedOffer);

      handleSbbQuickSelection(selectedOffer, quickSelectParam);
    }
  }, [language, sbbQuickSelection]);

  useEffect(() => {
    // Important: avoid triggering station search on dropdown selection.
    // Selecting an option updates URL params; we only want to search here when
    // hydrating from an external URL change (e.g. initial load / deep link),
    // not when params already match controlled selected values.
    if (fromParam && fromParam !== selectedFromStation?.value) {
      searchSbbStations(fromParam, "from", isZurichCard, sbbTransportApiContext);
    }
    if (toParam && toParam !== selectedToStation?.value) {
      searchSbbStations(toParam, "to", isZurichCard, sbbTransportApiContext);
    }
  }, [
    apiUrl,
    fromParam,
    toParam,
    selectedFromStation?.value,
    selectedToStation?.value,
    searchSbbStations,
    isZurichCard,
    sbbTransportApiContext
  ]);

  const onSbbStationInputChange = useCallback(
    (keyword: string) => searchSbbStations(keyword, "", isZurichCard, sbbTransportApiContext),
    [searchSbbStations, isZurichCard, sbbTransportApiContext]
  );

  // Set selected From and To stations
  useEffect(() => {
    if (selectedStationFrom?.[0]) {
      onChangeFromAutoComplete(selectedStationFrom?.[0]);
    }
  }, [selectedStationFrom]);
  useEffect(() => {
    if (selectedStationTo?.[0]) {
      onChangeToAutoComplete(selectedStationTo?.[0]);
    }
  }, [selectedStationTo]);

  const resetSelectedSbbActiveOffer = () => {
    if (!isObjectEmpty(sbbActiveOffer) || sbbActiveOffer?.code) {
      setSbbActiveOffer({});
      navigateSbb(`/${language}/transport/sbb`, { replace: true });
    }
  };

  const onChangeFromAutoComplete = (station: any) => {
    resetSelectedSbbActiveOffer();
    if (isAirlineSource) {
      skipAirlineRoutePrefillRef.current = true;
    }

    if (station?.name) {
      // Set selected From station to selected value
      setSelectedFromStation({ value: station?.name, label: station?.name, stationId: station?.station_id });

      if (toParam) {
        navigateSbb(`/${language}/transport/sbb?from=${station?.name}&to=${toParam}`, { replace: true });
      } else {
        navigateSbb(`/${language}/transport/sbb?from=${station?.name}`, { replace: true });
      }
    } else {
      // Set selected From station to null
      setSelectedFromStation(null);

      if (toParam) {
        navigateSbb(`/${language}/transport/sbb?to=${toParam}`, { replace: true });
      } else {
        navigateSbb(`/${language}/transport/sbb`, { replace: true });
      }
    }
  };

  const onChangeToAutoComplete = (station: any) => {
    resetSelectedSbbActiveOffer();
    if (isAirlineSource) {
      skipAirlineRoutePrefillRef.current = true;
    }

    if (station?.name) {
      // Set selected TO station to null
      setSelectedToStation({ value: station?.name, label: station?.name, stationId: station?.station_id });
      if (typeof station?.latitude === "number" && typeof station?.longitude === "number") {
        setSelectedToStationCoords({ lat: station.latitude, lng: station.longitude });
      } else {
        setSelectedToStationCoords(null);
      }

      if (fromParam) {
        navigateSbb(`/${language}/transport/sbb?from=${fromParam}&to=${station?.name}`, { replace: true });
      } else {
        navigateSbb(`/${language}/transport/sbb?to=${station?.name}`, { replace: true });
      }
    } else {
      // Set selected TO station to null
      setSelectedToStation(null);
      setSelectedToStationCoords(null);

      if (fromParam) {
        navigateSbb(`/${language}/transport/sbb?from=${fromParam}`, { replace: true });
      } else {
        navigateSbb(`/${language}/transport/sbb`, { replace: true });
      }
    }
  };

  const handleSbbQuickSelection = (offer: any, offerCode: string) => {
    setSbbActiveOffer(offer);
    setDisableCustomRouteAsync(true);
    // deselect custom routes
    selectedFromStation && setSelectedFromStation(null);
    selectedToStation && setSelectedToStation(null);
    setSelectedToStationCoords(null);

    if (isZurichCard) {
      navigateSbb(`/${language}/transport/zurich_card?qs=${offerCode}`, { replace: true });
    } else {
      navigateSbb(`/${language}/transport/sbb?qs=${offerCode}`, { replace: true });
    }
  };

  const handleSeePriceBtn = () => {
    const startDate: Date = selectedDate;
    startDate.setHours(sbbHours);
    startDate.setMinutes(sbbMinutes);

    getPriceSbbPrebook(
      {
        qualityOfService: serviceClass,
        direction: sbbActiveOffer?.code ? null : direction,
        start_date: format(startDate, "yyyy-MM-dd HH:mm"),
        offer_code: sbbActiveOffer?.code ? sbbActiveOffer?.code : sbbCustomRouteCode,
        milesAndMore: airlineSharedRoute.milesAndMore,
        ...(isAirlineSource
          ? {
              departureAddress: airlineSharedRoute.departureAddress,
              departureAddressId: airlineSharedRoute.departureAddressId,
              destinationAddress: airlineSharedRoute.destinationAddress,
              destinationAddressId: airlineSharedRoute.destinationAddressId
            }
          : {}),
        origin: selectedFromStation
          ? {
              name: selectedFromStation?.value,
              station_id: selectedFromStation?.stationId
            }
          : null,
        destination: selectedToStation
          ? {
              name: selectedToStation?.value,
              station_id: selectedToStation?.stationId
            }
          : null
      },
      isZurichCard,
      sbbTransportApiContext
    );
  };

  const handleBuyTicketBtn = () => {
    const offerCode = sbbActiveOffer?.code ? sbbActiveOffer?.code : sbbCustomRouteCode;

    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: offerCode ?? null
    });

    buySbbTicket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000), // get unix timestamp
        timeslot: `${sbbHours}:${sbbMinutes}`,
        offer_code: offerCode
      },
      navigate,
      isZurichCard,
      sbbTransportApiContext
    );
  };

  const handleValidationSeePriceBtn = (): boolean => {
    const isSelectedRoute: boolean = (selectedFromStation && selectedToStation) || sbbActiveOffer?.code;
    return (
      !isSelectedRoute || !checkIsItInFuture(selectedDate, sbbHours, sbbMinutes, 0) || checkForEmptyNames(passengerList)
    );
  };

  const renderPrebookErrorMsg = () => {
    if (checkIsItInFuture(selectedDate, sbbHours, sbbMinutes, 0) && !errorPrebook) {
      return null;
    }
    let errors: Array<any> = [];
    if (!checkIsItInFuture(selectedDate, sbbHours, sbbMinutes, 0)) {
      errors.push({
        errorMessage: t("transport.timeErrorMsg")
      });
    }
    if (errorPrebook?.fields) {
      errors = errors?.concat(errorPrebook?.fields);
    }

    if (!errors?.length) {
      errors.push({
        errorMessage: t("transport.prebookCustomError")
      });
    }

    return errors?.map((error: any, i: number) => (
      <div key={i} className="sbb__error-msg">
        {error?.field && `${error?.field} -`} {error?.errorMessage}
      </div>
    ));
  };

  const getDisabledStyleForTicketsForm = () => {
    if (prebookPriceInfo) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  return (
    <Grid
      className="sbb grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {sbbBookingError ? (
        <AppBookingError
          dataAttribute="sbb-booking-error-button"
          onButtonClick={() => toggleSbbBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={sbbData?.provider_title ?? sbbData?.title}
            shortDescription={sbbData?.provider_description}
            providerLogo={sbbData?.provider_logo}
          />

          {(sbbQuickSelection?.length > 0 || sbbCustomRouteCode) && !quickSelectionError ? (
            <Grid container>
              <Grid
                item
                sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
                md={6}
                xs={12}
              >
                <TransportRowAction
                  label={t("transport.departureDate")}
                  icon={dateIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppDatePickerCustom
                        selectedDate={selectedDate}
                        setSelectedDate={date => date != null && setSelectedDate(date)}
                        minDate={new Date()}
                      />
                    </Grid>
                  }
                />
                <TransportRowAction
                  label={t("transport.departureTime")}
                  icon={timeIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppTimeComponent
                        hours={sbbHours}
                        minutes={sbbMinutes}
                        handleHoursChange={(change: string, amount: number) =>
                          hoursUpdate(setSbbHours, change, amount, sbbHours)
                        }
                        handleMinutesChange={(change: string, amount: number) =>
                          updateMinutes(setSbbMinutes, setSbbHours, change, amount, sbbHours, sbbMinutes)
                        }
                        timeBoxSize="w-80"
                      />
                    </Grid>
                  }
                />
                {!isZurichCard && (
                  <>
                    <TransportRowAction
                      label={t("transport.serviceClass")}
                      icon={serviceClassIcon?.default}
                      actionComponent={
                        <TransportRowAction2Buttons
                          firstButtonLabel={"1"}
                          firstButtonHandle={() => setServiceClass(1)}
                          secondButtonLabel={"2"}
                          secondButtonHandle={() => setServiceClass(2)}
                          isFirstButtonActive={serviceClass === 1}
                        />
                      }
                    />
                    <TransportRowAction
                      label={t("transport.direction")}
                      icon={directionIcon?.default}
                      isDisabled={sbbActiveOffer?.code}
                      actionComponent={
                        <TransportRowAction2Buttons
                          firstButtonLabel={t("transport.oneWayBtn")}
                          firstButtonHandle={() => setDirection(outwardDirection)}
                          secondButtonLabel={t("transport.returnBtn")}
                          secondButtonHandle={() => setDirection(roundDirection)}
                          isFirstButtonActive={direction === outwardDirection}
                          fontSize="11px"
                        />
                      }
                    />
                  </>
                )}
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  {!isZurichCard && (
                    <AppCustomRouteAsync
                      setDisableCustomRouteAsync={setDisableCustomRouteAsync}
                      disableCustomRouteAsync={disableCustomRouteAsync}
                      selectedToStation={selectedToStation}
                      selectedFromStation={selectedFromStation}
                      onInputChange={onSbbStationInputChange}
                      onFocusCustomRouteAsync={resetSelectedSbbActiveOffer}
                      firstInputId="search-sbb-from-input"
                      secondInputId="search-sbb-to-input"
                      setSelectedFromStation={onChangeFromAutoComplete}
                      setSelectedToStation={onChangeToAutoComplete}
                    />
                  )}
                  {sbbQuickSelection?.length > 0 && (
                    <SbbQuickSelection
                      handleSbbQuickSelection={handleSbbQuickSelection}
                      quickSelectParam={quickSelectParam}
                    />
                  )}
                  {isAirlineTransferHub && destinationName ? (
                    <LastMileInfo
                      title={t("transport.lastMile.title")}
                      icon={<DirectionsWalkIcon fontSize="small" />}
                      destinationName={destinationName}
                      distanceMeters={lastMileRoute?.distance_meters ?? null}
                      durationSeconds={lastMileRoute?.duration_seconds ?? null}
                      googleMapsUrl={lastMileRoute?.google_maps_url ?? null}
                      isLoading={isLastMileRouteLoading}
                      error={lastMileRouteError}
                    />
                  ) : null}
                  <SbbPassengersForm isZurichCard={isZurichCard} />
                  {renderPrebookErrorMsg()}
                  <Grid
                    container={isAirlineSource}
                    item={!isAirlineSource}
                    xs={12}
                    sm={isAirlineSource ? undefined : 4}
                    sx={{
                      ...(isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineSource && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute="sbb-go-back-1"
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      sx={{
                        opacity: handleValidationSeePriceBtn() ? 0.8 : 1,
                        ...(isAirlineSource ? { flex: 1 } : {})
                      }}
                    >
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        disabled={handleValidationSeePriceBtn()}
                        dataAttribute="sbb-see-price"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                {prebookPriceInfo && (
                  <SbbSummary
                    onBuyTicket={handleBuyTicketBtn}
                    isZurichCard={isZurichCard}
                    cancellationPolicy={
                      (sbbActiveOffer?.code
                        ? sbbActiveOffer?.cancellation_policy
                        : sbbData?.details?.custom_route_cancellation_policy) || sbbData?.cancellation_policy
                    }
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showSbbPaymentModal && (
        <PaymentModal
          isOpen={showSbbPaymentModal}
          bookingResponseData={sbbBookingResponseData}
          togglePaymentModal={toggleSbbPaymentModal}
          redirectUrl="transport/sbb/confirmation"
        />
      )}
    </Grid>
  );
}
